import { $LegsWithOffset, $LegsWithThickness, $OakLegs, $SteelLegs, $TopCircle } from '../__generated/enhanced-model.js';
import { createRecipe, implementCustomExpressions } from '../__generated/implementation.js';
import { ColorOak } from '../__generated/model/index.js';
import { LEGS_LABEL_MAP, TOP_SHAPE_LABEL_MAP } from '../data/labels.js';
const AVAILABLE_HEIGHTS = [
    740,
    750,
    760,
    770
];
const HEIGHT_OPTIONS = AVAILABLE_HEIGHTS.map((height)=>({
        value: height,
        label: `${height / 10} cm`
    }));
export const tableExpressions = implementCustomExpressions({
    Table: {
        $availableLegTypes: (self)=>self.$top.$availableLegTypes,
        $availableMaterials: (self)=>self.$legs.$availableMaterials,
        $descriptionHeight: (self)=>({
                id: "ag3gIW",
                message: "H {0}cm",
                values: {
                    0: self.height / 10
                }
            }),
        $heightOptions: ()=>HEIGHT_OPTIONS,
        $price: (self)=>self.$top.$price + self.$legs.$price,
        $productName: ()=>({
                id: "eR6YQ/",
                message: "Custom Table"
            }),
        $topOptions: (s)=>{
            const commonProperties = {
                color: s.$top.color,
                edgeFinish: s.$top.edgeFinish,
                fenixVariant: s.$top.fenixVariant,
                material: s.$top.material,
                thickness: s.$top.thickness,
                topFinish: s.$top.topFinish
            };
            const diameter = $TopCircle.is(s.$top) ? {
                diameter: s.$top.diameter
            } : {};
            const lengthAndWidth = $TopCircle.is(s.$top) ? {} : {
                length: s.$top.length,
                width: s.$top.width
            };
            return [
                {
                    label: TOP_SHAPE_LABEL_MAP.TopCircle,
                    img: '/images/shapes/circle.webp',
                    recipes: [
                        createRecipe({
                            type: 'TopCircle',
                            ...commonProperties,
                            ...diameter
                        })
                    ]
                },
                {
                    label: TOP_SHAPE_LABEL_MAP.TopDanishOval,
                    img: '/images/shapes/danish-oval.webp',
                    recipes: [
                        createRecipe({
                            type: 'TopDanishOval',
                            ...commonProperties,
                            ...lengthAndWidth
                        })
                    ]
                },
                {
                    label: TOP_SHAPE_LABEL_MAP.TopHalfCircle,
                    img: '/images/shapes/half-circle.webp',
                    recipes: [
                        createRecipe({
                            type: 'TopHalfCircle',
                            ...commonProperties,
                            ...lengthAndWidth
                        })
                    ]
                },
                {
                    label: TOP_SHAPE_LABEL_MAP.TopHalfOval,
                    img: '/images/shapes/half-oval.webp',
                    recipes: [
                        createRecipe({
                            type: 'TopHalfOval',
                            ...commonProperties,
                            ...lengthAndWidth
                        })
                    ]
                },
                {
                    label: TOP_SHAPE_LABEL_MAP.TopOval,
                    img: '/images/shapes/oval.webp',
                    recipes: [
                        createRecipe({
                            type: 'TopOval',
                            ...commonProperties,
                            ...lengthAndWidth
                        })
                    ]
                },
                {
                    label: TOP_SHAPE_LABEL_MAP.TopRectangle,
                    img: '/images/shapes/rectangle.webp',
                    recipes: [
                        createRecipe({
                            type: 'TopRectangle',
                            ...commonProperties,
                            ...lengthAndWidth
                        })
                    ]
                }
            ];
        },
        $legOptions: (s)=>{
            const legOffset = $LegsWithOffset.is(s.$legs) ? {
                legOffset: s.$legs.legOffset
            } : {};
            const legThickness = $LegsWithThickness.is(s.$legs) ? {
                legThickness: s.$legs.legThickness
            } : {};
            /**
       * If the table top is wooden, the color of oak legs must match.
       * Otherwise, if changing from one type of oak legs to another, keep the color.
       */ const oakColor = ColorOak.is(s.$top.color) ? {
                color: s.$top.color
            } : $OakLegs.is(s.$legs) ? {
                color: s.$legs.color
            } : {};
            /**
       * If changing from one type of steel legs to another, keep the color
       */ const steelColor = $SteelLegs.is(s.$legs) ? {
                color: s.$legs.color
            } : {};
            return [
                // First wooden legs
                {
                    label: LEGS_LABEL_MAP.LegsBrago,
                    img: '/images/legs/brago.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsBrago',
                            ...legOffset,
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsCono,
                    img: '/images/legs/cono.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsCono',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsEbonuEik,
                    img: '/images/legs/ebonu-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsEbonuEik',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsEbonu3Eik,
                    img: '/images/legs/ebonu3-rond-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsEbonu3Eik',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsEbonu3DoubleEik,
                    img: '/images/legs/ebonu3-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsEbonu3DoubleEik',
                            ...legOffset,
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsElmo,
                    img: '/images/legs/elmo.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsElmo',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsElmoDouble,
                    img: '/images/legs/elmo-double.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsElmoDouble',
                            ...legOffset,
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsLivio,
                    img: '/images/legs/livio.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsLivio',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsLivioSchuinEik,
                    img: '/images/legs/livio-schuin-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsLivioSchuinEik',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsNovaroEik,
                    img: '/images/legs/novaro-rond-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsNovaroEik',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsNovaroDoubleEik,
                    img: '/images/legs/novaro-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsNovaroDoubleEik',
                            ...legOffset,
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsOslo,
                    img: '/images/legs/oslo.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsOslo',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsPapillon,
                    img: '/images/legs/papillon.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsPapillon',
                            ...legOffset,
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsPapillonStrak,
                    img: '/images/legs/papillon-strak.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsPapillonStrak',
                            ...legOffset,
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsRobuEik,
                    img: '/images/legs/robu-rond-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsRobuEik',
                            ...oakColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsRobuDoubleEik,
                    img: '/images/legs/robu-eik.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsRobuDoubleEik',
                            ...legOffset,
                            ...oakColor
                        })
                    ]
                },
                // Then steel legs
                {
                    label: LEGS_LABEL_MAP.Legs3D,
                    img: '/images/legs/3d.webp',
                    recipes: [
                        createRecipe({
                            type: 'Legs3D',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.Legs3DRonde,
                    img: '/images/legs/3d-ronde.webp',
                    recipes: [
                        createRecipe({
                            type: 'Legs3DRonde',
                            ...legThickness,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsAxel,
                    img: '/images/legs/axel.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsAxel',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsButterfly,
                    img: '/images/legs/butterfly.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsButterfly',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsClassico,
                    img: '/images/legs/classico.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsClassico',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsDiamond,
                    img: '/images/legs/diamond.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsDiamond',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsEbonuStaal,
                    img: '/images/legs/ebonu-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsEbonuStaal',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsEbonu3Staal,
                    img: '/images/legs/ebonu3-rond-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsEbonu3Staal',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsEbonu3DoubleStaal,
                    img: '/images/legs/ebonu3-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsEbonu3DoubleStaal',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsEbu,
                    img: '/images/legs/ebu.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsEbu',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsFalke,
                    img: '/images/legs/falke.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsFalke',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsFly,
                    img: '/images/legs/fly.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsFly',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsHarper,
                    img: '/images/legs/harper.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsHarper',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsHarper3,
                    img: '/images/legs/harper3.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsHarper3',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsJulio3,
                    img: '/images/legs/julio3.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsJulio3',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsJulio4,
                    img: '/images/legs/julio4.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsJulio4',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsLivioSchuinStaal,
                    img: '/images/legs/livio-schuin-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsLivioSchuinStaal',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsMadrid,
                    img: '/images/legs/madrid.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsMadrid',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsMPootRecht,
                    img: '/images/legs/mpoot-recht.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsMPootRecht',
                            ...legThickness,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsMPootSchuin,
                    img: '/images/legs/mpoot-schuin.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsMPootSchuin',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsNewly,
                    img: '/images/legs/newly.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsNewly',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsNovaroStaal,
                    img: '/images/legs/novaro-rond-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsNovaroStaal',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsNovaroDoubleStaal,
                    img: '/images/legs/novaro-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsNovaroDoubleStaal',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsOdil3,
                    img: '/images/legs/odil3.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsOdil3',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsOdil4,
                    img: '/images/legs/odil4.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsOdil4',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsOxford,
                    img: '/images/legs/oxford.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsOxford',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsPino,
                    img: '/images/legs/pino.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsPino',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsRafino,
                    img: '/images/legs/rafino.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsRafino',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsRobuStaal,
                    img: '/images/legs/robu-rond-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsRobuStaal',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsRobuDoubleStaal,
                    img: '/images/legs/robu-staal.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsRobuDoubleStaal',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsRoia,
                    img: '/images/legs/roia.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsRoia',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsTPoot,
                    img: '/images/legs/tpoot.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsTPoot',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsTPootRonde,
                    img: '/images/legs/tpoot-ronde.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsTPootRonde',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsTrapezium,
                    img: '/images/legs/trapezium.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsTrapezium',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsTwist3,
                    img: '/images/legs/twist3.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsTwist3',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsTwist4,
                    img: '/images/legs/twist4.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsTwist4',
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsUPoot,
                    img: '/images/legs/upoot.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsUPoot',
                            ...legOffset,
                            ...legThickness,
                            ...steelColor
                        })
                    ]
                },
                {
                    label: LEGS_LABEL_MAP.LegsXPoot,
                    img: '/images/legs/xpoot.webp',
                    recipes: [
                        createRecipe({
                            type: 'LegsXPoot',
                            ...legOffset,
                            ...steelColor
                        })
                    ]
                }
            ];
        }
    }
});

//# sourceMappingURL=table.js.map